/**
 * Who is at this browser, decided against Clerk and the real API before
 * anything else renders.
 *
 * The rule the shell exists to keep: a browser without a signed in student
 * never sees the app frame. Not for a frame's worth of time while a request is
 * in flight, and not because a request failed. Until this resolves the page
 * shows the brand on an empty sheet, and every outcome other than "signed in"
 * leads to the sign in screen, the profile step, or an honest "could not reach
 * the server".
 *
 * Since ADR-010 there are two parties to ask, in this order:
 *
 *   1. Clerk, loaded in the page: is anybody signed in here at all? If not,
 *      signed out, and the API is not asked.
 *   2. GET /api/me, with Clerk's token: is there a student behind this sign
 *      in, and may this device be the one they use? The one device rule is
 *      the API's, so only the API can say "signed in elsewhere".
 *
 * An account an admin has not approved yet is shown that it is waiting, and
 * nothing else (migration 0012). One that was declined is signed out and told.
 *
 * Every account has a plan: Free until it takes another (PRD v3.2, day 35),
 * so a signed in student always lands in the app.
 *
 * One shortcut, 2 October 2026: a page that finds a live session token in
 * Clerk's cookie asks the API with it at once, and shows the app when the API
 * says yes, without waiting for Clerk's script. The API has verified a real
 * session token for a real student, so this is not a signed out browser seeing
 * the frame; the one gap is the minute a token outlives a sign out that was
 * still in flight, and Clerk's listener closes it as soon as Clerk loads.
 *
 * A browser the API refuses is signed out at Clerk too, so the two never
 * disagree for longer than one request: a sign in the portal will not honour
 * should not sit in Clerk's storage looking like one it will.
 */

import type { AccessErrorCode, PlanSummary, Profile } from "@ls/shared";
import { ApiUnavailable, expect, type ApiReply, type Transport } from "../api";
import { BRAND_NAME, HELP_EMAIL } from "../brand";
import { ClerkUnavailable, type ClerkLike } from "../auth/clerk";
import { accessErrorContract, meResponseContract } from "../contracts.generated";

/** Why the sign in screen is showing, when there is more to say than "sign in". */
export type SignedOutReason = "signed_in_elsewhere" | "session_ended" | "suspended" | "declined";

export type Gate =
  | { readonly kind: "loading" }
  | { readonly kind: "signed_in"; readonly profile: Profile; readonly plan: PlanSummary }
  /** Signed up, and waiting for an admin to let them in (migration 0012). */
  | { readonly kind: "awaiting_approval"; readonly email: string; readonly name: string | null }
  /** Signed in at Clerk with no account here yet: the profile step. */
  | { readonly kind: "needs_profile"; readonly email: string | null }
  | { readonly kind: "signed_out"; readonly reason: SignedOutReason | null }
  | { readonly kind: "unavailable"; readonly detail: string };

async function quietly(action: () => Promise<unknown>): Promise<void> {
  try {
    await action();
  } catch {
    // Signing out locally cannot usefully fail: the next load asks again.
  }
}

/** What the API's refusal means for this browser. */
async function refused(code: AccessErrorCode, clerk: ClerkLike): Promise<Gate> {
  switch (code) {
    case "server_misconfigured":
      return { kind: "unavailable", detail: "Sign in is unavailable right now." };
    case "signed_in_elsewhere":
      await quietly(() => clerk.signOut());
      return { kind: "signed_out", reason: "signed_in_elsewhere" };
    case "suspended":
      await quietly(() => clerk.signOut());
      return { kind: "signed_out", reason: "suspended" };
    case "access_declined":
      await quietly(() => clerk.signOut());
      return { kind: "signed_out", reason: "declined" };
    default:
      // unauthenticated, or profile_required from a route that is not /me.
      await quietly(() => clerk.signOut());
      return { kind: "signed_out", reason: "session_ended" };
  }
}

/** What /me's 200 means, as a gate. */
function readMe(reply: ApiReply): Gate {
  const me = expect(reply, meResponseContract, "/me");
  if (me.state === "needs_profile") return { kind: "needs_profile", email: me.email };
  if (me.state === "awaiting_approval") return { kind: "awaiting_approval", email: me.email, name: me.name };
  return { kind: "signed_in", profile: me.profile, plan: me.plan };
}

/**
 * One pass of the order above. Throws nothing: every failure is a Gate.
 *
 * early is a session token the page already holds, from the cookie Clerk keeps
 * (auth/hint.ts). With one, GET /me is sent at once, in the same moments
 * Clerk's script is starting, instead of after it: on a phone that is a whole
 * round trip off the first screen. The API checks it exactly as it checks a
 * token from Clerk. If the answer is a plain "signed in" there is nothing for
 * Clerk to add and the gate does not wait for it. Any other answer, including
 * the 401 of a token that lapsed on the way, falls back to the order above,
 * asking Clerk first and then again with its fresh token, so an early token
 * can only ever save time and never decide a refusal.
 */
export async function resolveGate(
  api: Transport,
  loadClerk: () => Promise<ClerkLike>,
  early: string | null = null,
): Promise<Gate> {
  if (early) {
    try {
      const reply = await api.get("/me", { token: early });
      if (reply.status === 200) return readMe(reply);
    } catch {
      // The early try is only ever an optimisation: ask again the careful way.
    }
  }

  let clerk: ClerkLike;
  try {
    clerk = await loadClerk();
  } catch (error) {
    return unavailable(error);
  }
  if (!clerk.session) return { kind: "signed_out", reason: null };

  try {
    const reply = await api.get("/me");
    if (reply.status === 200) return readMe(reply);
    return await refused(expect(reply, accessErrorContract, "/me").error, clerk);
  } catch (error) {
    return unavailable(error);
  }
}

/** This browser only, at Clerk. Other devices were already signed out by the one device rule. */
export async function signOut(clerk: ClerkLike): Promise<Gate> {
  await quietly(() => clerk.signOut());
  return { kind: "signed_out", reason: null };
}

function unavailable(error: unknown): Gate {
  const detail =
    error instanceof ApiUnavailable || error instanceof ClerkUnavailable
      ? error.message
      : error instanceof Error
        ? error.message
        : "Unknown error.";
  return { kind: "unavailable", detail };
}

/** The wording for each reason. The API sends codes, never prose. */
export const SIGNED_OUT_WORDING: Readonly<Record<SignedOutReason, { title: string; body: string }>> = {
  signed_in_elsewhere: {
    title: "You signed in on another device",
    body: `${BRAND_NAME} keeps you signed in on one device at a time, so this one was signed out. If that was not you, sign in again here, which signs the other device out.`,
  },
  session_ended: {
    title: "You were signed out",
    body: `Sign in again to carry on. If you signed in on another device, that is why: ${BRAND_NAME} keeps you signed in on one device at a time.`,
  },
  suspended: {
    title: "This account is suspended",
    body: `Write to ${HELP_EMAIL} if you think this is a mistake.`,
  },
  declined: {
    title: "Your request was not approved",
    body: `Write to ${HELP_EMAIL} if you think this is a mistake.`,
  },
};
